import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, TrendingUp, Layers, Linkedin, Instagram, Lock, Play, Pause, Volume2, Tags, Info } from 'lucide-react';
import { TelegramIcon } from '../components/icons/TelegramIcon';
import { RedditIcon } from '../components/icons/RedditIcon';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { GlassCard, GlassButton, AnimatedCounter } from '../components/LiquidComponents';
import { ImageLightbox } from '../components/ImageLightbox';
import { useData } from '../contexts/DataContext';
import { Project } from '../types';
import { resolveProjectSlug } from '../utils/projectSlug';

import 'swiper/css';
import 'swiper/css/pagination';

const XLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor" width="20" height="20">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Безопасная функция для обработки ссылок
const getHostname = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return url;
  }
};

const ensureHttp = (url: string) => {
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

// A link should render only when it carries a real, non-whitespace URL. Trim first,
// then check length, so a whitespace-only string counts as empty (no dimmed placeholder).
const hasUrl = (url?: string | null): boolean => typeof url === 'string' && url.trim().length > 0;

// Server-injected header data (api/page-meta.ts) for this exact slug, if any.
// Present only when the page was served by the project meta function, i.e. a
// direct visit to /portfolio/:slug — absent on client-side navigation.
const readProjectBootstrap = (slug?: string): ProjectBootstrap | null => {
  if (typeof window === 'undefined' || !slug) return null;
  const bootstrap = window.__PROJECT_BOOTSTRAP__;
  if (!bootstrap || bootstrap.slug !== slug) return null;
  return bootstrap;
};

// Header-only stand-in rendered from the bootstrap payload while the Supabase
// fetch is pending or has failed. Text is byte-identical to both the
// server-injected pre-mount block and the final mounted header (single h1,
// description rendered once), so crawler snapshots at any moment agree.
const BootstrapHeader: React.FC<{ bootstrap: ProjectBootstrap }> = ({ bootstrap }) => (
  <div className="pt-36 md:pt-44 pb-24 min-h-screen container mx-auto px-4 md:px-6">
    <h1 className="mb-6 text-3xl md:text-5xl font-bold text-zinc-900 dark:text-white">{bootstrap.name}</h1>
    <p className="max-w-2xl text-lg md:text-xl leading-relaxed text-zinc-600 dark:text-zinc-300 font-light">
      {bootstrap.description}
    </p>
    <p className="mt-5 text-sm font-light text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300">
      {bootstrap.role} · {bootstrap.stage}
    </p>
  </div>
);

const GlassAudioPlayer = ({ src }: { src: string }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const rafRef = React.useRef<number | null>(null);
  const isSeekingRef = React.useRef(false);

  const stopRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  // Read the live playhead straight from the audio element every animation frame so the
  // progress fill + thumb track playback at 60fps with no transition lag or trailing ghost.
  const syncFromAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur = audio.duration;
    setCurrentTime(audio.currentTime);
    if (Number.isFinite(dur) && dur > 0 && !isSeekingRef.current) {
      setProgress((audio.currentTime / dur) * 100);
    }
  };

  const tick = () => {
    syncFromAudio();
    rafRef.current = requestAnimationFrame(tick);
  };

  const startRaf = () => {
    if (rafRef.current === null) rafRef.current = requestAnimationFrame(tick);
  };

  React.useEffect(() => stopRaf, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      stopRaf();
    } else {
      try {
        await audio.play();
        setIsPlaying(true);
        startRaf();
      } catch {
        setIsPlaying(false);
        stopRaf();
      }
    }
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio && Number.isFinite(audio.duration)) setDuration(audio.duration);
  };

  const finishSeek = () => { isSeekingRef.current = false; };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const newProgress = Number(event.target.value);
    isSeekingRef.current = true;
    try {
      audio.currentTime = (newProgress / 100) * audio.duration;
      setProgress(newProgress);
      setCurrentTime(audio.currentTime);
    } catch {
      finishSeek();
    }
  };

  const handleEnded = () => { setIsPlaying(false); setProgress(0); setCurrentTime(0); finishSeek(); stopRaf(); };
  const handleError = () => { setIsPlaying(false); setProgress(0); finishSeek(); stopRaf(); };

  const formatTime = (value: number) => {
    const safe = Number.isFinite(value) && value > 0 ? value : 0;
    const minutes = Math.floor(safe / 60);
    const seconds = Math.floor(safe % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full flex items-center gap-4 p-3 pr-5 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 backdrop-blur-md">
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={syncFromAudio}
        onEnded={handleEnded}
        onError={handleError}
        onSeeked={finishSeek}
        className="hidden"
      />
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause Audio Overview' : 'Play Audio Overview'}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-zinc-900 dark:bg-white text-white dark:text-black hover:scale-105 transition-transform shadow-lg shrink-0"
      >
        {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
      </button>
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="flex justify-between items-center text-[10px] uppercase font-bold tracking-widest text-zinc-400">
          <span className="flex items-center gap-1"><Volume2 size={12} /> Audio Overview</span>
          <span className="tabular-nums tracking-normal text-zinc-900 dark:text-white [.nardogrey_&]:text-white" aria-label={`${formatTime(currentTime)} of ${formatTime(duration)}`}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="any"
          value={progress}
          onChange={handleSeek}
          onMouseUp={finishSeek}
          onTouchEnd={finishSeek}
          aria-label="Audio progress"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          style={{ background: `linear-gradient(to right, currentColor 0%, currentColor ${progress}%, color-mix(in srgb, currentColor 16%, transparent) ${progress}%, color-mix(in srgb, currentColor 16%, transparent) 100%)` }}
          className="audio-slider w-full cursor-pointer text-zinc-900 dark:text-white [.nardogrey_&]:text-white focus:outline-none focus:ring-0"
        />
      </div>
    </div>
  );
};

export const ProjectDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { projects, loading } = useData();
  const [project, setProject] = useState<Project | null>(null);
  const [showcaseView, setShowcaseView] = useState<'desktop' | 'mobile'>('desktop');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const showcaseSwiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    if (!loading && projects.length > 0) {
      const foundProject = projects.find((p) => {
        const projectSlug = resolveProjectSlug(p);
        return projectSlug === slug || p.id === slug;
      });
      setProject(foundProject || null);
    }
  }, [slug, projects, loading]);

  useEffect(() => {
    if (!project) return;
    const desktopImages = (project.desktopShowcaseImages ?? []).filter(Boolean);
    const mobileImages = (project.mobileShowcaseImages ?? []).filter(Boolean);
    const hasDesktop = desktopImages.length > 0;
    const hasMobile = mobileImages.length > 0;
    const preferredView = project.defaultShowcaseView === 'mobile' ? 'mobile' : 'desktop';
    if (preferredView === 'mobile') {
      setShowcaseView(hasMobile ? 'mobile' : hasDesktop ? 'desktop' : 'mobile');
    } else {
      setShowcaseView(hasDesktop ? 'desktop' : hasMobile ? 'mobile' : 'desktop');
    }
  }, [project?.id, project?.defaultShowcaseView, project?.desktopShowcaseImages, project?.mobileShowcaseImages]);

  if (loading || !project) {
    // Direct visit: the server already resolved this slug and injected its
    // header data, so render that instead of a spinner — and keep rendering it
    // if the client-side Supabase fetch fails (a failed fetch must never
    // swallow a page the server knows is real). Client-side navigation has no
    // bootstrap and keeps the original loading / not-found behavior.
    const bootstrap = readProjectBootstrap(slug);
    if (bootstrap) return <BootstrapHeader bootstrap={bootstrap} />;
    if (loading) return <div className="min-h-screen pt-32 text-center text-white">Loading project...</div>;
    return <div className="min-h-screen pt-32 text-center text-white">Project not found. <button onClick={() => navigate('/portfolio')} className="text-zinc-900 dark:text-white underline underline-offset-2">Back</button></div>;
  }

  const projectName = project.title || project.name || "Untitled";
  const safeWebsiteUrl = ensureHttp((project.websiteUrl || '').trim());
  const safeCrunchbaseUrl = ensureHttp((project.crunchbase || '').trim());
  const crunchbaseLabel = project.crunchbaseText?.trim() || 'View on Crunchbase';
  // Only platforms with a real URL survive the filter, so empty ones are simply absent
  // (never a dimmed/disabled icon). If nothing survives, the whole Community block is hidden.
  const socialLinks = [
    { key: 'instagram', url: project.socials?.instagram, icon: <Instagram size={20} /> },
    { key: 'x', url: project.socials?.x, icon: <XLogo className="w-5 h-5" /> },
    { key: 'linkedin', url: project.socials?.linkedin, icon: <Linkedin size={20} /> },
    { key: 'telegram', url: project.socials?.telegram, icon: <TelegramIcon size={20} /> },
    { key: 'reddit', url: project.socials?.reddit, icon: <RedditIcon size={20} /> },
  ].filter((s) => hasUrl(s.url));
  const isMobileShowcase = showcaseView === 'mobile';
  // Screenshots are exported from Figma already framed (e.g. inside iPhone bezels),
  // so we render them cleanly with no extra device shell, border, ring, gradient or
  // background. Natural sizing + object-contain keeps each image fully visible and undistorted.
  const showcaseImageClass = isMobileShowcase ? 'max-h-[78vh]' : 'max-h-[70vh]';
  const desktopShowcaseImages = (project.desktopShowcaseImages ?? []).filter(Boolean) as string[];
  const mobileShowcaseImages = (project.mobileShowcaseImages ?? []).filter(Boolean) as string[];
  const hasDesktopShowcase = desktopShowcaseImages.length > 0;
  const hasMobileShowcase = mobileShowcaseImages.length > 0;
  const activeShowcaseImages = isMobileShowcase ? mobileShowcaseImages : desktopShowcaseImages;
  const hasShowcase = hasDesktopShowcase || hasMobileShowcase;
  const showDesktopTab = hasDesktopShowcase;
  const showMobileTab = hasMobileShowcase;
  const hasShowcaseNavigation = activeShowcaseImages.length > 1;
  const showcaseBreakpoints = isMobileShowcase
    ? {
        0: { slidesPerView: 1.12, spaceBetween: 16, slidesOffsetAfter: 12 },
        640: { slidesPerView: 1.08, spaceBetween: 18, slidesOffsetAfter: 16 },
        768: { slidesPerView: 2, spaceBetween: 22 },
        1024: { slidesPerView: 3, spaceBetween: 24 },
        1280: { slidesPerView: 3, spaceBetween: 28 }
      }
    : {
        0: { slidesPerView: 1, spaceBetween: 18 },
        768: { slidesPerView: 2, spaceBetween: 22 },
        1024: { slidesPerView: 3, spaceBetween: 24 },
        1280: { slidesPerView: 3, spaceBetween: 28 }
      };

  const handleRequestDeck = () => {
    const event = new CustomEvent('open-deck-modal', { detail: { projectName: projectName } });
    window.dispatchEvent(event);
  };

  return (
    <div className="pt-36 md:pt-44 pb-24 min-h-screen container mx-auto px-4 md:px-6 relative z-10 overflow-x-hidden">
      
      {/* ИСПРАВЛЕННАЯ КНОПКА: 
          1. z-[999] - чтобы быть поверх всего.
          2. pointer-events-auto - чтобы клики точно проходили.
          3. touch-manipulation - для улучшения тач-отклика.
          4. Иконка и текст имеют pointer-events-none, чтобы клик всегда падал на саму кнопку.
      */}
      <button
        onClick={() => navigate('/portfolio')}
        className="mb-8 flex items-center gap-2 text-zinc-400 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white [.nardogrey_&]:text-white transition-colors text-sm uppercase tracking-wider font-medium cursor-pointer relative z-[999] pointer-events-auto w-fit p-2 -ml-2 rounded-lg hover:bg-white/5 touch-manipulation active:scale-95"
      >
        <ArrowLeft size={16} className="pointer-events-none" /> 
        <span className="pointer-events-none">Back to Portfolio</span>
      </button>

      {/* Header Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            {project.logoUrl ? (
               <img src={project.logoUrl} alt={projectName} className="h-20 md:h-28 w-auto object-contain flex-shrink-0" />
            ) : (
               <div className="h-20 md:h-28 px-5 rounded-xl bg-zinc-800 flex items-center justify-center text-white font-bold text-2xl md:text-3xl flex-shrink-0">
                 {projectName.charAt(0)}
               </div>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-zinc-900 dark:text-white">{projectName}</h1>
          </div>

          <p className="max-w-2xl text-lg md:text-xl leading-relaxed text-zinc-600 dark:text-zinc-300 font-light">
            {project.description}
          </p>
        </div>

        <div className="flex flex-col justify-center gap-4">
          {hasUrl(project.websiteUrl) && (
            <a href={safeWebsiteUrl} target="_blank" rel="noreferrer" className="w-full">
              <GlassButton className="w-full justify-center">
                Visit Website <ExternalLink size={16} />
              </GlassButton>
            </a>
          )}
          <GlassButton variant="secondary" onClick={handleRequestDeck} className="w-full justify-center border-dashed">
            <Lock size={16} /> Request Pitch Deck
          </GlassButton>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {/* Metrics Column */}
        <div className="md:col-span-2 space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
          <GlassCard hoverEffect={false} hoverScale={false} className="p-6 md:p-8">
            <h3 className="text-xl font-light text-zinc-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300"/> Key Metrics
            </h3>
            {Array.isArray(project.metrics) && project.metrics.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {project.metrics.map((metric, idx) => (
                  <div key={idx} className="p-4 bg-zinc-50 dark:bg-white/5 rounded-2xl border border-zinc-100 dark:border-white/5 [.nardogrey_&]:bg-white/[0.04]">
                    <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">{metric.label}</p>
                    <div className="text-2xl md:text-3xl font-light text-zinc-900 dark:text-white">
                        <AnimatedCounter value={metric.value} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 italic">No public metrics available for this stage.</p>
            )}
          </GlassCard>

          {project.content?.trim() && (
            <GlassCard hoverEffect={false} hoverScale={false} className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-light text-zinc-900 dark:text-white flex items-center gap-2">
                  <Layers size={20} className="text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300"/> Project Deep Dive
                </h3>
              </div>

              <div className="prose dark:prose-invert max-w-none text-zinc-600 dark:text-zinc-300 font-light whitespace-pre-line">
                {project.content}
              </div>
            </GlassCard>
          )}

          {project.audioUrl && <GlassAudioPlayer src={project.audioUrl} />}
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
          <GlassCard hoverEffect={false} hoverScale={false} className="p-6">
            <h3 className="text-xl font-light text-zinc-900 dark:text-white mb-6 flex items-center gap-2">
              <Tags size={20} className="text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300"/> Focus Areas
            </h3>
            {/* <ul>/<li> keeps word boundaries between adjacent pills for
                CSS-blind text extractors; preflight zeroes the list styles. */}
            <ul className="flex flex-wrap gap-2 list-none m-0 p-0">
              {project.tags?.map(tag => (
                <li key={tag} className="px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap bg-black/[0.04] dark:bg-white/[0.05] [.nardogrey_&]:bg-white/[0.05] border border-black/5 dark:border-white/10 [.nardogrey_&]:border-white/10 text-zinc-600 dark:text-zinc-300 [.nardogrey_&]:text-zinc-300">
                  {tag}
                </li>
              ))}
            </ul>
          </GlassCard>

          <GlassCard hoverEffect={false} hoverScale={false} className="p-6">
            <h3 className="text-xl font-light text-zinc-900 dark:text-white mb-6 flex items-center gap-2">
              <Info size={20} className="text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300"/> Project Info
            </h3>
            <div className="space-y-4">
              {/* Labels/values are real <div>s (identical to the span.block
                  rendering) so CSS-blind extraction reads "Role / Founder"
                  instead of "RoleFounder". */}
              <div className="pb-2.5 border-b border-zinc-100 dark:border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-2">Role</div>
                <div className="text-sm font-medium text-zinc-900 dark:text-white [.nardogrey_&]:text-white">
                  {project.role || 'Project Lead'}
                </div>
              </div>

              <div className="pb-2.5 border-b border-zinc-100 dark:border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-2">Stage</div>
                <div className="text-sm font-medium text-zinc-900 dark:text-white [.nardogrey_&]:text-white">
                  {typeof project.stage === 'string' ? project.stage : 'Concept'}
                </div>
              </div>

              {hasUrl(project.websiteUrl) && (
                <div className="pb-2.5 border-b border-zinc-100 dark:border-white/5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-2">Website</div>
                  <a
                    href={safeWebsiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium text-zinc-900 dark:text-white [.nardogrey_&]:text-white hover:underline underline-offset-2"
                  >
                    {getHostname(safeWebsiteUrl)}
                  </a>
                </div>
              )}

              {hasUrl(project.crunchbase) && (
                <div className="pb-2.5 border-b border-zinc-100 dark:border-white/5">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-2">Crunchbase</div>
                  <a
                    href={safeCrunchbaseUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block text-sm font-medium text-zinc-900 dark:text-white [.nardogrey_&]:text-white hover:underline underline-offset-2"
                    title={crunchbaseLabel}
                  >
                    {crunchbaseLabel}
                  </a>
                </div>
              )}

              {/* Social Links Section — render only platforms with a non-empty URL; hide the whole block if none */}
              {socialLinks.length > 0 && (
                <div className="pt-1">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 [.nardogrey_&]:text-zinc-300 mb-3">Community</div>
                  <div className="flex gap-4">
                    {socialLinks.map((s) => (
                      <a
                        key={s.key}
                        href={ensureHttp((s.url ?? '').trim())}
                        target="_blank"
                        rel="noreferrer"
                        className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
                      >
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        </div>
      </div>

      {hasShowcase && (
        <div className="mt-12 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex flex-col items-center gap-4 mb-6 text-center">
            <h3 className="text-xl font-light text-zinc-900 dark:text-white">Showcase</h3>
            {(showDesktopTab || showMobileTab) && (
              <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-md shadow-sm w-fit">
                {showDesktopTab && (
                  <button
                    type="button"
                    onClick={() => setShowcaseView('desktop')}
                    className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest rounded-full transition-all ${!isMobileShowcase ? 'bg-white/20 text-zinc-900 dark:text-white shadow-lg' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'}`}
                    aria-pressed={!isMobileShowcase}
                  >
                    Desktop Version
                  </button>
                )}
                {showMobileTab && (
                  <button
                    type="button"
                    onClick={() => setShowcaseView('mobile')}
                    className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest rounded-full transition-all ${isMobileShowcase ? 'bg-white/20 text-zinc-900 dark:text-white shadow-lg' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'}`}
                    aria-pressed={isMobileShowcase}
                  >
                    Mobile Version
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="relative group w-full">
            {/* Desktop: full-width track that starts flush with the content container's left edge.
                Mobile: a centred phone-width column. No base centering classes here, so the desktop
                track can never be re-centered by stylesheet cascade ordering. */}
            <div
              className={`w-full ${isMobileShowcase ? 'mx-auto max-w-sm sm:max-w-md md:max-w-none md:mx-0' : ''}`}
            >
              <Swiper
                key={showcaseView}
                className={`showcase-swiper w-full pb-10 ${isMobileShowcase ? 'px-2 sm:px-3' : ''}`}
                modules={[Pagination]}
                pagination={{ clickable: true }}
                onSwiper={(swiper) => {
                  showcaseSwiperRef.current = swiper;
                }}
                grabCursor
                speed={650}
                slidesPerView={isMobileShowcase ? 1.12 : 3}
                spaceBetween={isMobileShowcase ? 16 : 24}
                breakpoints={showcaseBreakpoints}
              >
                {activeShowcaseImages.map((image, index) => (
                  <SwiperSlide key={`${image}-${index}`} className="flex !h-auto items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setLightboxIndex(index)}
                      className="group block w-full focus:outline-none"
                      aria-label={`Open ${projectName} showcase image ${index + 1}`}
                    >
                      <img
                        src={image}
                        alt={`${projectName} showcase ${index + 1}`}
                        className={`mx-auto block h-auto w-full ${showcaseImageClass} object-contain`}
                        loading="lazy"
                      />
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            {hasShowcaseNavigation && (
              <>
                <button
                  type="button"
                  onClick={() => showcaseSwiperRef.current?.slidePrev()}
                  className="hidden md:flex items-center justify-center absolute z-20 left-2 md:-left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/10 bg-black/30 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  aria-label="Previous showcase image"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => showcaseSwiperRef.current?.slideNext()}
                  className="hidden md:flex items-center justify-center absolute z-20 right-2 md:-right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full border border-white/10 bg-black/30 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  aria-label="Next showcase image"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 rotate-180" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {hasShowcase && (
        <ImageLightbox
          isOpen={lightboxIndex !== null}
          images={activeShowcaseImages}
          startIndex={lightboxIndex ?? 0}
          onClose={() => setLightboxIndex(null)}
          label={`${projectName} showcase`}
        />
      )}
    </div>
  );
};
